import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getNames } from "country-list";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../../../assets/logos/text-logo-white.svg";
import { ROUTES } from "../../../../routes";
import Button from "../../../common/Button/Button";
import Tooltip from "../../../common/Tooltip/Tooltip";
import styles from "../../../layouts/OnboardLayout/OnboardLayout.module.scss";
import { FORMIK_REGEX, Messages } from "../../../../utils/constants";
import * as Yup from "yup";
import InputFormik from "../../../common/Formik/Input/InputFormik";
import Select from "../../../common/Formik/Select/Select";
import { useDispatch } from "react-redux";
import { validationMessage } from "../../../../Helper/helperfunction";
import { useDebounce } from "../../../customHook/useDebeunce";
import { usernameEmailExistAction } from "../../../../features/user/userSlice";
import {
  isOTPVerified,
  saveSignUpDetails,
} from "../../../../features/persist/persistSlice";
import SuccessText from "../../../common/Formik/SuccessText/SuccessText";
import ErrorText from "../../../common/Formik/ErrorText/ErrorText";



const Register = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const [userFind, setUserFind] = useState("");
  const [emailFind, setEmailFind] = useState("");
  const userFindRef = useRef("");
  const emailFindRef = useRef("");
  userFindRef.current = userFind;
  emailFindRef.current = emailFind;

  const [search, setSearch] = useState("");
  const [emailexist, setEmailExist] = useState("");
  const debounced = useDebounce(search, 500);
  const debouncedEmail = useDebounce(emailexist, 500);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        fname: Yup.string()
          .label("First Name")
          .required(validationMessage("First Name"))
          .min(1)
          .max(25)
          .matches(
            FORMIK_REGEX.ALPHA,
            "Only alphabets, spaces, and periods are allowed.. No special characters, or numeric are allowed"
          ),
        lname: Yup.string()
          .label("Last Name")
          .required(validationMessage("Last Name"))
          .min(1)
          .max(25)
          .matches(
            FORMIK_REGEX.ALPHA,
            "Only alphabets, spaces, and periods are allowed.. No special characters, or numeric are allowed"
          ),
        username: Yup.string()
          .label("Username")
          .required(validationMessage("Username"))
          .min(4)
          .max(50)
          .matches(FORMIK_REGEX.USERNAME, Messages?.USERNAMEVALIDATION),
        email: Yup.string()
          .label("Email")
          .required(validationMessage("Email"))
          .max(50)
          .matches(FORMIK_REGEX.EMAIL, Messages?.invalidEmail),
        isUSCitizen: Yup.string().required(
          validationMessage("US Citizen status")
        ),
        country: Yup.string().when("isUSCitizen", {
          is: "No",
          then: (schema) => schema.required(validationMessage("Country")),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    []
  );

  const countryOptions = useMemo(
    () =>
      [...getNames()]
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ label: name, value: name })),
    []
  );

  const generatePartneroCustomerKey = () => {
    const key =
      "key_" +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    return key;
  };

  const formik = useFormik({
    initialValues: {
      fname: "",
      lname: "",
      username: "",
      email: "",
      isUSCitizen: "",
      country: "",
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      if (userFindRef.current === false || emailFindRef.current === false) {
        return;
      }
      const partneroKey = generatePartneroCustomerKey();

      const dataToSave = {
        ...values,
        partneroCustomerKey: partneroKey,
      };
      dispatch(saveSignUpDetails(dataToSave));

      if (window.po && typeof window.po === "function") {
        try {
          window.po("customers", "signup", {
            data: {
              key: values.email,
              name: values.fname,
              email: values.email,
            },
          });
        } catch (error) {
          console.error(" Error tracking customer in Partnero:", error);
        }
      } else {
        console.warn("⚠️ Partnero 'po' function not found on window.");
      }
      history(ROUTES.EMAIL_VERIFICATION);
    },
  });

  useEffect(() => {
    dispatch(isOTPVerified(false));
  }, []);



  useEffect(() => {
    if (debounced.length >= 4 && FORMIK_REGEX.USERNAME.test(debounced)) {
      setUserFind("");
      dispatch(
        usernameEmailExistAction({
          isEmail: false,
          username: debounced,
        })
      )
        .then((response) => {
          if (response?.status === 200) {
            setUserFind(response?.data?.data);
          }
        })
        .catch(() => {
          setUserFind("");
        });
    } else {
      setUserFind("");
    }
  }, [debounced, dispatch]);

  useEffect(() => {
    if (FORMIK_REGEX.EMAIL.test(debouncedEmail)) {
      setEmailFind("");
      dispatch(
        usernameEmailExistAction({
          isEmail: true,
          username: debouncedEmail,
        })
      )
        .then((response) => {
          if (response?.status === 200) {
            setEmailFind(response?.data?.data);
          }
        })
        .catch(() => {
          setEmailFind("");
        });
    } else {
      setEmailFind("");
    }
  }, [debouncedEmail, dispatch]);

  const formPassesValidation = useMemo(() => {
    try {
      validationSchema.validateSync(formik.values, { abortEarly: false });
      return true;
    } catch {
      return false;
    }
  }, [formik.values, validationSchema]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value, true);
    setSearch(value);
  };

  const handleInputEmailChange = (event) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value, true);
    setEmailExist(value);
  };

  return (
    <div className={styles.onboard_box}>
      <h2>Create an account</h2>
     
      <form
        method="POST"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <InputFormik
          control="input"
          type="text"
          className={styles.input}
          name="fname"
          value={formik.values.fname}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Please enter your first name"
          label="First Name"
          formik={formik}
          maxLength={30}

        />
        <InputFormik
          control="input"
          type="text"
          className={styles.input}
          name="lname"
          value={formik.values.lname}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder="Please enter your last name"
          label="Last Name"
          maxLength={30}
          formik={formik}
        />
        <InputFormik
          control="input"
          type="text"
          className={styles.input}
          name="username"
          value={formik.values.username}
          placeholder="e.g. user_name.01"
          label="Username"
          onChange={handleInputChange}
          onBlur={formik.handleBlur}
          maxLength={30}
          formik={formik}
          icon={
            <Tooltip content="Limited to 30 Characters and must contain only letters, numbers, periods and underscores." />
          }
        >
         
          {userFind === false && (
            <ErrorText>{"Username is not available"}</ErrorText>
          )}
        
        </InputFormik>
        <InputFormik
          control="input"
          type="email"
          className={styles.input}
          name="email"
          value={formik.values.email}
          onChange={handleInputEmailChange}
          onBlur={formik.handleBlur}
          placeholder="Please enter your email"
          label="Email"
          formik={formik}
          maxLength={50}
        >
         
          {emailFind === false && (
            <ErrorText>{"Email is not available"}</ErrorText>
          )}
        
        </InputFormik>
        <Select
          instanceId="register-is-us-citizen"
          name="isUSCitizen"
          label="Are You a US Citizen ?"
          placeholder="Are You a US Citizen?"
          className={styles.input}
          formik={formik}
          isSearchable={false}
          options={[
            { label: "Yes", value: "Yes" },
            { label: "No", value: "No" },
          ]}
          onChange={(option) => {
            if (!option) return;
            formik.setFieldValue("isUSCitizen", option.value, true);
            if (option.value !== "No") {
              formik.setFieldValue("country", "", true);
            }
            formik.setFieldTouched("country", false, false);
          }}
          value={
            formik.values.isUSCitizen === "Yes"
              ? { label: "Yes", value: "Yes" }
              : formik.values.isUSCitizen === "No"
                ? { label: "No", value: "No" }
                : null
          }
        />
        {formik.values.isUSCitizen === "No" && (
          <div className={`${styles.input} ${styles.country_native}`}>
            <label htmlFor="register-country-select">Country</label>
            <select
              id="register-country-select"
              name="country"
              className={styles.country_native_control}
              value={formik.values.country}
              onChange={(e) => {
                const { value } = e.target;
                formik.setFieldValue("country", value, true);
                formik.setFieldTouched("country", true, false);
              }}
              onBlur={formik.handleBlur}
            >
              <option value="">Select your country</option>
              {countryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {formik.touched.country &&
              formik.errors.country &&
              !String(formik.values.country || "").trim() && (
                <ErrorText>{formik.errors.country}</ErrorText>
              )}
          </div>
        )}
        <p className={styles.link_text}>
          By creating an account, I agree to STBL’s{" "}
          <a
            href="https://www.stbl.io/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms
          </a>{" "}
          and{" "}
          <a
            href="https://www.stbl.io/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          .
        </p>

        <div className={styles.submit_btn}>
          <Button
            fluid
            type="button"
            onClick={(e) => {
              formik.handleSubmit(e);
            }}
            disabled={!formPassesValidation || formik.isSubmitting}
          >
            {formik.isSubmitting ? "Processing..." : "Continue"}
          </Button>
        </div>
        <p className={styles.link_text}>
          Already have an account?{" "}
          <Link className={styles.bold} to={ROUTES.LOGIN}>
            Login
          </Link>
        </p>
      </form>
    </div>
  );
};

export default Register;
